import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RotateCcw } from "lucide-react";
import { toast } from "@/components/ui/sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichTextEditor } from "@/features/action-plans/components/RichTextEditor";

import {
  type InvitationEmailKind,
  useInvitationEmail,
  useInviteMember,
  useSaveInvitationEmail,
} from "../api";
import { apiErrorDetail } from "../lib/errors";
import {
  GroupRole,
  isPromoteConfirmation,
  type InviteMemberBody,
  type InvitePromoteConfirmation,
} from "../types";

/** Does the editor's HTML hold any text, beyond empty paragraphs and breaks? */
function hasText(html: string): boolean {
  return html.replace(/<[^>]*>|&nbsp;/g, "").trim() !== "";
}

function kindFor(role: GroupRole): InvitationEmailKind {
  return role === GroupRole.LocalPartner ? "local_partner" : "member";
}

/**
 * Invite a member or local partner into a group, showing the email first.
 *
 * Modelled on the stakeholder consultation (ConsultationEmailEditor): the
 * group's body for the chosen role is loaded into the editor, the inviter can
 * reword it, and « Envoyer l'invitation » sends that wording with this one
 * invitation. « Enregistrer comme modèle » — shown only to the group's owner
 * and the CQDD — stores it as the group's body instead. The Django invite
 * window (`#inviteMemberModal`) is the same composer.
 *
 * Handles the two server responses of the `invite-member` action: a created
 * invitation, or a confirmation prompt for a local partner who already has an
 * account (re-submitted with `confirm_promote`). Every invitation stays
 * pending until answered; the group page lists it (GroupInvitationList).
 */
export function InviteMemberForm({
  groupId,
  canSaveTemplate,
  canPickRole,
}: {
  groupId: string;
  /** May the reader store this group's wording — its owner, or the CQDD.
   * Decided server-side, by the rule the template endpoint enforces. */
  canSaveTemplate: boolean;
  /** May the reader invite a local partner into this group. A local partner
   * of the group adds Members only — the server turns the role back — so the
   * Django window hides the picker for them, group by group, and so does this
   * one: otherwise it would load the local-partner wording for an invitation
   * sent as a Member. Decided server-side. */
  canPickRole: boolean;
}) {
  const { t } = useTranslation();
  const invite = useInviteMember(groupId);

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<GroupRole>(GroupRole.Member);
  const [promote, setPromote] = useState<InvitePromoteConfirmation | null>(null);

  const kind = kindFor(role);
  const template = useInvitationEmail(groupId, kind);
  const saveTemplate = useSaveInvitationEmail(groupId, kind);

  const [html, setHtml] = useState("");
  const [loadedKind, setLoadedKind] = useState<InvitationEmailKind | null>(null);
  // RichTextEditor is uncontrolled after mount — it seeds its DOM once — so
  // every time the body has to change under the caret (a load, a reset, the
  // server's sanitized echo) it is remounted by bumping this.
  const [seed, setSeed] = useState(0);
  const reseed = (next: string) => {
    setHtml(next);
    setSeed((n) => n + 1);
  };

  // Seed once the body arrives, and again whenever the role changes: each
  // role has its own body.
  useEffect(() => {
    if (template.data && loadedKind !== kind) {
      reseed(template.data.html);
      setLoadedKind(kind);
    }
  }, [template.data, kind, loadedKind]);

  const loaded = loadedKind === kind;
  const isDirty = loaded && html !== template.data?.html;

  const selectRole = (next: GroupRole) => {
    if (kindFor(next) !== kind && isDirty) {
      if (!window.confirm(t("partners.invitationEmail.discard"))) return;
    }
    setRole(next);
  };

  const send = (body: InviteMemberBody) => {
    invite.mutate(body, {
      onSuccess: (result) => {
        if (isPromoteConfirmation(result)) {
          setPromote(result);
          return;
        }
        setPromote(null);
        toast.success(t("partners.invite.sent", { email: result.email }));
        setEmail("");
        if (role !== GroupRole.Member) {
          setRole(GroupRole.Member);
        } else if (template.data) {
          // The next invitation starts from the group's wording again.
          reseed(template.data.html);
        }
      },
      onError: (err) => toast.error(apiErrorDetail(err) ?? t("partners.actionFailed")),
    });
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    send({
      email: email.trim(),
      assigned_group_role: role,
      // A body that never loaded, or that was cleared to nothing, sends nothing:
      // the server then falls back to the group's wording, as in Django.
      ...(loaded && hasText(html) ? { email_body: html } : {}),
    });
  };

  const confirmPromote = () => {
    if (!promote) return;
    send({
      email: promote.email,
      assigned_group_role: GroupRole.LocalPartner,
      confirm_promote: true,
    });
  };

  const onSaveTemplate = () =>
    saveTemplate.mutate(html, {
      // Show what was actually stored: the server strips anything outside its
      // whitelist, so echoing the submitted HTML would misrepresent what sends.
      onSuccess: (data) => {
        reseed(data.html);
        toast.success(t("partners.invitationEmail.saved"));
      },
      onError: (err) =>
        toast.error(apiErrorDetail(err) ?? t("partners.invitationEmail.saveFailed")),
    });

  return (
    <div className="space-y-4">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="invite-email">{t("partners.invite.emailLabel")}</Label>
          <Input
            id="invite-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("partners.invite.emailPlaceholder")}
          />
        </div>
        {canPickRole && (
          <div className="space-y-1.5">
            <Label htmlFor="invite-role">{t("partners.invite.roleLabel")}</Label>
            <select
              id="invite-role"
              value={role}
              onChange={(e) => selectRole(e.target.value as GroupRole)}
              className="flex min-h-touch w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value={GroupRole.Member}>{t("partners.invite.roleMember")}</option>
              <option value={GroupRole.LocalPartner}>
                {t("partners.invite.roleLocalPartner")}
              </option>
            </select>
          </div>
        )}

        <div className="space-y-1.5">
          <Label>{t("partners.invitationEmail.body")}</Label>
          <p className="text-sm text-muted-foreground">
            {t("partners.invitationEmail.help")}
          </p>
          {template.isError ? (
            <p className="text-sm text-destructive">
              {t("partners.invitationEmail.loadFailed")}
            </p>
          ) : loaded ? (
            <RichTextEditor
              key={seed}
              value={html}
              onChange={setHtml}
              ariaLabel={t("partners.invitationEmail.body")}
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              {t("partners.invitationEmail.loading")}
            </p>
          )}
        </div>

        <p className="rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
          {t("partners.invitationEmail.existingAccountNote")}
        </p>

        <div className="flex flex-wrap items-center justify-end gap-2">
          {loaded && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mr-auto"
              onClick={() => reseed(template.data?.default ?? "")}
            >
              <RotateCcw aria-hidden />
              {t("partners.invitationEmail.reset")}
            </Button>
          )}
          {canSaveTemplate && loaded && (
            <Button
              type="button"
              variant="outline"
              onClick={onSaveTemplate}
              disabled={saveTemplate.isPending}
            >
              {t("partners.invitationEmail.saveAsTemplate")}
            </Button>
          )}
          <Button type="submit" disabled={invite.isPending || email.trim() === ""}>
            {invite.isPending ? t("partners.invite.sending") : t("partners.invite.submit")}
          </Button>
        </div>
      </form>

      {promote && (
        <div
          role="alertdialog"
          aria-labelledby="promote-title"
          className="rounded-md border border-input bg-muted/40 p-4"
        >
          <p id="promote-title" className="font-medium">
            {t("partners.invite.confirmPromoteTitle")}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("partners.invite.confirmPromoteBody")}
          </p>
          {/* Who the account is, as the Django modal shows it. */}
          <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            {(
              [
                ["promoteName", promote.name || "-"],
                ["promoteEmail", promote.email],
                ["promoteEnterprise", promote.enterprise],
                ["promoteAddress", promote.address],
                ["promotePhone", promote.phone],
              ] as const
            )
              .filter(([, value]) => value)
              .map(([key, value]) => (
                <div key={key} className="contents">
                  <dt className="font-medium text-muted-foreground">
                    {t(`partners.invite.${key}`)}
                  </dt>
                  <dd className="min-w-0 break-words">{value}</dd>
                </div>
              ))}
          </dl>
          <div className="mt-3 flex items-center gap-2">
            <Button size="sm" disabled={invite.isPending} onClick={confirmPromote}>
              {t("partners.invite.confirmPromote")}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setPromote(null)}>
              {t("partners.invite.cancel")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
